import type { Client } from 'urql';
import config from '@packages/config';
import { logger } from '@packages/shared/src/utils/logger/logger';
import { ShopConfigDocument } from '@packages/gql/generated/backoffice/ShopConfigDocument';
import type { LocaleInputType, ShopConfigQuery } from '@packages/gql/generated/backoffice/graphql';

type AppConfigSections = NonNullable<ShopConfigQuery['Config']>;

/**
 * Sections are always present; fields are `null` when unset in the backoffice or when
 * their query section errored — consumers fall back to the static `config` then.
 */
export type AppConfigData = {
  i18n: NonNullable<AppConfigSections['i18n']>;
  tagManager: NonNullable<NonNullable<AppConfigSections['tracking']>['tagManager']>;
};

export const EMPTY_APP_CONFIG: AppConfigData = {
  i18n: { activeLocales: null, defaultLocale: null },
  tagManager: { gtmId: null, gtmSrc: null },
};

export const CC_CONSUMER_SHOPCONFIG_COOKIE = 'cc-consumer-shopconfig';

/** Temporary rollout guard; while off the backoffice isn't queried and the static config applies. */
export const isCcConsumerShopConfigEnabled = (cookieValue: string | undefined): boolean =>
  process.env.ENABLE_CC_CONSUMER_SHOPCONFIG === 'true' || cookieValue === 'true';

/**
 * Tag manager props for `TrackingProvider`. `||` on purpose: an emptied backoffice field
 * must fall back too, not override with ''.
 */
export const getTagManagerProps = (appConfig: AppConfigData | null) => ({
  tagmanagerId: appConfig?.tagManager.gtmId || config.tracking.tagmanagerId,
  domain: config.tracking.tagmanagerDomain,
  src: appConfig?.tagManager.gtmSrc || config.tracking.tagmanagerSrc,
});

/**
 * Uncached. The client is injected because the app router's request-memoized client and
 * the pages router's plain server client can't be shared. Throws when the backoffice
 * returns no Shop config, so a cache around it never stores the failure.
 */
export const fetchAppConfig = async (client: Client, locale: string): Promise<AppConfigData> => {
  const { data, error } = await client.query(ShopConfigDocument, {
    locale: locale.replace('-', '_') as LocaleInputType,
    draft: false,
  });

  if (!data?.Config) {
    throw error ?? new Error('App config query returned no Config');
  }

  // Keep partial data instead of bailing — one section's failure must not blank the rest.
  if (error) {
    logger.warn(
      { err: error, locale, gateway: process.env.BACKOFFICE_GQL_GATEWAY },
      'Error while fetching app config data (returning partial data)',
    );
  }

  return {
    i18n: data.Config.i18n ?? EMPTY_APP_CONFIG.i18n,
    tagManager: data.Config.tracking?.tagManager ?? EMPTY_APP_CONFIG.tagManager,
  };
};
